import React, { useState, useRef, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useFetch, usePost, usePut, useClick, StickyElement } from '../../../components/component-items/helpers'
import { toast } from 'react-toastify'
import { Col, Row } from 'react-bootstrap'
import { store } from '../../../store'
import { BackgroundLayer, OrderOptions, OrderInfo, Dropdown } from './styles'

// Enums
import { OrderStatus } from '../../../enums'

// Components
import CardLoading from '../../../components/component-items/loading-popover'
import { DropdownItem } from '../../../components/component-items/export-tools'
import { AddressModal } from '../../../components/modals/address-modal'
import GlobalStyles from '../../../components/component-items/styles'
import { PermissionGate } from '../../../components/rbac'

// Cards
import { TableList } from './tablelist'
import { StatusBar } from './status-bar'
import { OrderDetailCard } from './order-detail-card'
import { AddressCard } from '../../../components/cards/address-card'
import { NotificationList } from '../../../components/notifications'
import { HandlingUnitList } from './handling-units'
import { Shipping } from './shipping'
import { Billing } from './billing'
import { Slips } from './slips'
import { ShipmentManagementModal } from './shipment-details'
import { isNonParcel, isNonParcelMode, isWillCall } from '../../../types/transport'
import SplitOrdersCard from './split-orders-card'

// Modals
import { ReturnModal } from './return-modal'
import { CreateASNModal } from './create-asn-modal'
import { ShippingOptionsModal } from './shipping-options-modal'
import { OrderStatusModal } from './order-status-modal'
import { PauseOrderModal } from './pause-order-modal'
import { OverrideShippingRateModal } from './override-shipping-rate-modal'
import { CancelOrderModal } from './cancel-order-modal'
import { HotOrderModal } from './hot-order-modal'
import { IntegrationModal } from './integration-modal'

import { UploadProductModal, UploadLabelModal, UploadDocumentModal } from './upload-modals'
import { OrderNotesModal, EditProjectNotes } from './order-notes-modal'
import { LotNumbersModal } from './lot-numbers-modal'
import { WarehouseShippingOrderModal } from './940_transaction'
import { WarehouseShippingAdviceModal } from './945_transaction'

// Keyboard Shortcuts
import { connectShortcuts, handleOrderPermissions } from '../../../keyboard-shortcuts'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faShippingFast,
  faStickyNote,
  faLayerGroup,
  faLayerPlus,
  faExchange,
  faHeartRate,
  faRampLoading,
  faPrint,
  faTruck,
  faChevronRight,
  faFileSignature,
  faClipboardCheck,
  faCloudUpload,
  faFileUpload,
  faCubes,
  faFileAlt,
  faBoxFull,
  faTools,
  faFire,
  faRoute,
  faPlug,
  faBarcodeRead,
  faRulerCombined,
  faTrashAlt,
  faPause,
  faClone,
  faEnvelopeOpenText,
  faBox,
  faInboxIn,
  faInventory,
  faInboxOut,
  faCode,
} from '@fortawesome/pro-duotone-svg-icons'

import { FulfillmentItemUpdate, OrderOptionProps, OrderProps, SplitOrdersGroup } from './types'
import { SSCCBarcodeModal } from '../../edi/sscc_barcodes'
import { normalizeFulfillmentGroupResponse } from './split-orders.utils'
import GenericModal from '../../../components/component-items/modal'

/**
 * Fetches the EDI packing slip data from the API
 * @param orderId The ID of the order to fetch the packing slip for
 * @param loadingToast The toast ID for loading indicator
 */
import { generatePackingSlip } from '../../../utils/packingSlip'

const OrderOption = ({ url, onClick, icon, title, arrow, children, node }: OrderOptionProps) => {
  return (
    <li ref={node} onClick={onClick}>
      <a href={url || '#'}>
        <span className="icon">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="text">{title}</span>
        {arrow ? <FontAwesomeIcon icon={faChevronRight} style={{ color: '#595d6e' }} /> : null}
      </a>
      {children}
    </li>
  )
}

export const OrderPage = () => {
  const globalState = useContext(store)
  const navigate = useNavigate()
  const {
    state: { csrf, userIsWarehouse, backorderManagementEnabled },
  } = globalState

  const order = useParams<{ id: string }>().id || ''

  // State for triggering refetch when shipment details are updated
  const [refetchKey, setRefetchKey] = useState(0)
  const handleShipmentUpdate = () => setRefetchKey((prev) => prev + 1)

  const orderPageUrl = `/api/order/${order}/status/${refetchKey ? `?_=${refetchKey}` : ''}`
  const {
    response: resp,
    loaded,
    error,
    placeholder,
  }: { response: OrderProps; loaded: boolean; error?: any; placeholder?: string } = useFetch(orderPageUrl, {})

  const orderStatus = loaded ? resp.orderStatus : { orderStatus: null }
  const statusId = loaded ? orderStatus?.statusId : OrderStatus.Processing
  const handlingUnitDetails = loaded ? resp.handlingUnitDetails : []
  const serviceLevels = loaded ? resp.serviceLevels : []
  const isWarehouse = loaded ? resp.isWarehouse : false
  const canCancel = loaded ? resp.canCancel : false
  const rateAmount = loaded ? resp.rateAmount : 0.0
  const transportMode = loaded ? resp.transport_mode : 'parcel'
  const shipment = loaded ? resp.shipment : null
  const b2b = loaded ? resp.b_to_b : false
  const b2bPackingSlipMode = loaded ? resp.b2b_packing_slip_mode : null
  const shipBefore = loaded ? resp.ship_before : ''
  const shipAfter = loaded ? resp.ship_after : ''
  const [fulfillmentGroup, setFulfillmentGroup] = useState<SplitOrdersGroup | null>(null)

  useEffect(() => {
    if (!loaded) {
      return
    }

    setFulfillmentGroup(normalizeFulfillmentGroupResponse(resp?.fulfillmentGroup))
  }, [loaded, resp])

  const orderIsSplit = Boolean(fulfillmentGroup?.orders && fulfillmentGroup.orders.length > 1)

  const [shipDropdown, setShipDropdown] = useState(false)
  const shipNode = useRef<HTMLLIElement | null>(null)
  useClick(shipNode, setShipDropdown)

  const [statusDropdown, setStatusDropdown] = useState(false)
  const node = useRef<HTMLLIElement | null>(null)
  useClick(node, setStatusDropdown)

  const [uploadDropdown, setUploadDropdown] = useState(false)
  const uploadNode = useRef<HTMLLIElement | null>(null)
  useClick(uploadNode, setUploadDropdown)

  const [advancedDropdown, setAdvancedDropdown] = useState(false)
  const advancedNode = useRef<HTMLLIElement | null>(null)
  useClick(advancedNode, setAdvancedDropdown)

  const [cancelModal, setCancelModal] = useState(false)
  const [hotModal, setHotModal] = useState(false)
  const [notesModal, setNotesModal] = useState(false)
  const [pauseModal, setPauseModal] = useState(false)
  const [addressModal, setAddressModal] = useState(false)
  const [uploadProductModal, setUploadProductModal] = useState(false)
  const [uploadLabelModal, setUploadLabelModal] = useState(false)
  const [uploadDocumentModal, setUploadDocumentModal] = useState(false)
  const [manualStatusModal, setManualStatusModal] = useState(false)
  const [overrideShippingRateModal, setOverrideShippingRateModal] = useState(false)
  const [shippingOptionsModal, setShippingOptionsModal] = useState(false)
  const [returnModal, setReturnModal] = useState(false)
  const [asnModal, setAsnModal] = useState(false)
  const [integrationModal, setIntegrationModal] = useState(false)
  const [shipmentModal, setShipmentModal] = useState(false)
  const [ediInboundModal, setEdiInboundModal] = useState(false)
  const [ediOutboundModal, setEdiOutboundModal] = useState(false)
  const [barcodeModal, setBarcodeModal] = useState(false)
  const [lotNumbersModal, setLotNumbersModal] = useState(false)
  const [policyModal, setPolicyModal] = useState(false)

  // Fulfillment audit modal state
  const [fulfillmentAuditModal, setFulfillmentAuditModal] = useState(false)
  const [fulfillmentAuditLoading, setFulfillmentAuditLoading] = useState(false)
  const [fulfillmentAuditError, setFulfillmentAuditError] = useState<string | null>(null)
  const [fulfillmentAuditPayload, setFulfillmentAuditPayload] = useState<any>(null)

  const handleOpenFulfillmentAudit = async () => {
    setFulfillmentAuditModal(true)
    setFulfillmentAuditLoading(true)
    setFulfillmentAuditError(null)
    setFulfillmentAuditPayload(null)

    try {
      const response = await fetch(`/api/order/${order}/audit/`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const payload = await response.json().catch(() => null)

      if (!response.ok) {
        throw new Error((payload && (payload.detail || payload.error)) || 'Unable to load fulfillment audit.')
      }

      setFulfillmentAuditPayload(payload)
    } catch (error: any) {
      const message = typeof error?.message === 'string' ? error.message : 'Unable to load fulfillment audit.'
      setFulfillmentAuditError(message)
    } finally {
      setFulfillmentAuditLoading(false)
    }
  }

  const handleFulfillmentItemsUpdate = async (items: FulfillmentItemUpdate[]) => {
    const payload = items
      .filter((item) => item.groupLineId !== undefined && item.groupLineId !== null)
      .map((item) => ({
        groupLineId: item.groupLineId,
        quantity: Number(item.quantity ?? 0).toFixed(2),
        disposition: item.disposition,
      }))

    if (!payload.length) {
      throw new Error('No fulfillment items to update.')
    }

    const response = await fetch(`/api/order/${order}/fulfillment_items/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrf || '',
      },
      body: JSON.stringify({ items: payload }),
    })

    if (!response.ok) {
      const errorPayload = await response.json().catch(() => null)
      const message =
        (errorPayload && (errorPayload.detail || errorPayload.error)) || 'Unable to update fulfillment items.'

      throw new Error(message)
    }

    const json = await response.json().catch(() => null)

    const updatedGroup = normalizeFulfillmentGroupResponse(json?.fulfillmentGroup ?? json)
    setFulfillmentGroup(updatedGroup)

    toast.success('Canonical order items updated.')
  }

  // Fulfillment policy assignment state
  const [availablePolicies, setAvailablePolicies] = useState<{ id: number; name: string }[]>([])
  const [selectedPolicyId, setSelectedPolicyId] = useState<number | null>(null)
  const [policyLoading, setPolicyLoading] = useState(false)

  const showPolicySelector = loaded && backorderManagementEnabled && userIsWarehouse && statusId < 100

  useEffect(() => {
    if (!showPolicySelector || !resp) {
      setSelectedPolicyId(null)
      return
    }
    setSelectedPolicyId(resp.fulfillmentPolicy?.id ?? null)
  }, [showPolicySelector, resp?.fulfillmentPolicy?.id])

  useEffect(() => {
    if (!showPolicySelector) {
      setAvailablePolicies([])
      return
    }
    const controller = new AbortController()
    fetch(`/api/order/${order}/available_fulfillment_policies/`, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => setAvailablePolicies(Array.isArray(data) ? data : []))
      .catch(() => {
        if (!controller.signal.aborted) setAvailablePolicies([])
      })
    return () => controller.abort()
  }, [showPolicySelector, order])

  const handlePolicyChange = async (newPolicyId: number) => {
    setPolicyLoading(true)
    try {
      const response = await fetch(`/api/order/${order}/assign_fulfillment_policy/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf || '' },
        body: JSON.stringify({ fulfillment_policy_id: newPolicyId }),
      })
      if (!response.ok) {
        const err = await response.json().catch(() => null)
        throw new Error(err?.detail || 'Failed to assign fulfillment policy.')
      }
      const result = await response.json()
      setSelectedPolicyId(result.id)
      toast.success(`Fulfillment policy changed to "${result.name}".`)
    } catch (error: any) {
      toast.error(error.message || 'Failed to assign fulfillment policy.')
    } finally {
      setPolicyLoading(false)
    }
  }

  // Keyboard Shortcuts:
  // Attaches listener on order page for keyboard shortcuts
  // such as: Label Purchase, Pack, Confirm Dimensions, etc
  useEffect(() => {
    if (loaded && userIsWarehouse) {
      const handleKeyDown = (event: any) => {
        handleOrderPermissions(
          order,
          resp.packStatus.fully_packed,
          resp.packStatus.needs_dimensions,
          resp.packStatus.labeled,
          navigate
        )
        connectShortcuts(event, order, 'order', navigate)
      }

      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [loaded])

  return (
    <>
      <StatusBar />
      <Row>
        <Col lg={3} xl={2}>
          <StickyElement>
            <OrderOptions>
              <ul>
                {loaded && isWarehouse && statusId >= OrderStatus.Picked && statusId < OrderStatus.LabelPurchased ? (
                  resp.packStatus.fully_packed ? (
                    <OrderOption
                      onClick={() => setShipDropdown(!shipDropdown)}
                      icon={faShippingFast}
                      title={'Pack & Ship'}
                      arrow={true}
                      node={shipNode}
                    >
                      {shipDropdown ? (
                        <Dropdown>
                          <GlobalStyles.Tooltip data-title="Directs to Pack page">
                            <DropdownItem url={`/order/${order}/pack/`} icon={faFileAlt} text={'Pack Order'} />
                          </GlobalStyles.Tooltip>
                          {resp.packStatus.serial_number_enabled ? (
                            <DropdownItem
                              url={`/order/${order}/serial-numbers/`}
                              icon={faBarcodeRead}
                              text={'Add Serial Number'}
                            />
                          ) : null}
                          {!resp.packStatus.needs_serial_numbers ? (
                            <GlobalStyles.Tooltip data-title="Directs to Confirm Dimensions page">
                              <DropdownItem
                                url={`/order/${order}/confirm-dimensions/`}
                                icon={faRulerCombined}
                                text={'Confirm Dimensions'}
                              />
                            </GlobalStyles.Tooltip>
                          ) : null}
                          {resp.packStatus.fully_packed &&
                          !isNonParcel(resp.transport_mode) &&
                          (!resp.packStatus.contracting_party || resp.packStatus.contracting_party === 'shipper') &&
                          !resp.packStatus.needs_serial_numbers &&
                          !resp.packStatus.needs_dimensions ? (
                            <GlobalStyles.Tooltip data-title="Directs to Purchase Label page">
                              <DropdownItem
                                url={`/order/${order}/label-purchase/`}
                                icon={faPrint}
                                text={'Purchase Label'}
                              />
                            </GlobalStyles.Tooltip>
                          ) : null}
                        </Dropdown>
                      ) : null}
                    </OrderOption>
                  ) : (
                    <>
                      {resp.batch_type !== 'prepack' && (
                        <GlobalStyles.Tooltip data-title="Directs to Pack Order page">
                          <OrderOption url={`/order/${order}/pack/`} icon={faShippingFast} title={'Pack & Ship'} />
                        </GlobalStyles.Tooltip>
                      )}
                    </>
                  )
                ) : null}
                {loaded && isWarehouse && resp.batch ? (
                  <GlobalStyles.Tooltip data-title="Displays order barcode with picking locations">
                    <OrderOption url={`/api/order/${order}/picklist/`} icon={faBoxFull} title={'Order Picklist'} />
                  </GlobalStyles.Tooltip>
                ) : null}
                {loaded && isWarehouse && statusId >= OrderStatus.Backordered && !resp.batch ? (
                  <GlobalStyles.Tooltip data-title="Batches order and sets status to Processing">
                    <OrderOption
                      onClick={() => usePost(`/api/order/${order}/batch/`, {}, csrf)}
                      icon={faLayerGroup}
                      title={'Batch Manually'}
                    />
                  </GlobalStyles.Tooltip>
                ) : null}
                {loaded &&
                isWarehouse &&
                statusId >= OrderStatus.Processing &&
                statusId < OrderStatus.LabelPurchased ? (
                  <PermissionGate segment="outbound" minLevel="admin">
                    <GlobalStyles.Tooltip data-title="Unbatches order and returns status back to Received">
                      <OrderOption url={`/api/order/${order}/unbatch/`} icon={faLayerGroup} title={'Unbatch'} />
                    </GlobalStyles.Tooltip>
                  </PermissionGate>
                ) : null}
                {loaded && isWarehouse && statusId >= OrderStatus.Processing && statusId < OrderStatus.Shipped ? (
                  <PermissionGate segment="outbound" minLevel="admin">
                    <GlobalStyles.Tooltip data-title="Re-batching puts this order in its own batch, but maintains the shelf transactions, handling units and labels associated with it.">
                      <OrderOption
                        onClick={() => usePost(`/api/order/${order}/re_batch/`, {}, csrf)}
                        icon={faLayerPlus}
                        title={'Re-batch'}
                      />
                    </GlobalStyles.Tooltip>
                  </PermissionGate>
                ) : null}
                {loaded && statusId < OrderStatus.Shipped ? (
                  <OrderOption
                    onClick={() => setUploadDropdown(!uploadDropdown)}
                    icon={faCloudUpload}
                    title={'Upload'}
                    arrow={true}
                    node={uploadNode}
                  >
                    {uploadDropdown ? (
                      <Dropdown>
                        {statusId < OrderStatus.Processing ? (
                          <GlobalStyles.Tooltip data-title="Add products through .csv file">
                            <DropdownItem
                              onClick={() => setUploadProductModal(true)}
                              icon={faCubes}
                              text={'Upload Products'}
                            />
                          </GlobalStyles.Tooltip>
                        ) : null}
                        <GlobalStyles.Tooltip data-title="Upload labels manually">
                          <DropdownItem
                            onClick={() => setUploadLabelModal(true)}
                            icon={faFileUpload}
                            text={'Upload Labels'}
                          />
                        </GlobalStyles.Tooltip>
                        <GlobalStyles.Tooltip data-title="Add any additional documents">
                          <DropdownItem
                            onClick={() => setUploadDocumentModal(true)}
                            icon={faFileAlt}
                            text={'Upload Documents'}
                          />
                        </GlobalStyles.Tooltip>
                      </Dropdown>
                    ) : null}
                  </OrderOption>
                ) : null}
                {statusId >= OrderStatus.Packed ? (
                  <GlobalStyles.Tooltip data-title="Packing slips">
                    {loaded && (resp.integration === 'edi' || (b2b && b2bPackingSlipMode !== 'generic_label')) ? (
                      <PermissionGate segment="outbound" minLevel="operator">
                        <OrderOption
                          onClick={() => generatePackingSlip(order)}
                          icon={faPrint}
                          title={'Packing Slips'}
                        />
                      </PermissionGate>
                    ) : (
                      <OrderOption
                        url={`/api/order/${order}/packing_slips/?download=1`}
                        icon={faPrint}
                        title={'Packing Slips'}
                      />
                    )}
                  </GlobalStyles.Tooltip>
                ) : null}

                {loaded ? (
                  <GlobalStyles.Tooltip data-title="Manage shipment details, tracking, and costs">
                    <OrderOption onClick={() => setShipmentModal(true)} icon={faTruck} title={'Shipment Details'} />
                  </GlobalStyles.Tooltip>
                ) : null}
                {statusId >= OrderStatus.LabelPurchased && isWarehouse ? (
                  <GlobalStyles.Tooltip data-title="Downloads shipping labels">
                    <OrderOption
                      url={`/api/order/${order}/print_labels/`}
                      icon={faStickyNote}
                      title={'Shipping Labels'}
                    />
                  </GlobalStyles.Tooltip>
                ) : null}
                {isWarehouse ? (
                  <OrderOption
                    onClick={() => setStatusDropdown(!statusDropdown)}
                    icon={faHeartRate}
                    title={'Change Status'}
                    arrow={true}
                    node={node}
                  >
                    {statusDropdown ? (
                      <Dropdown>
                        <PermissionGate segment="outbound" minLevel="admin">
                          {canCancel && (
                            <DropdownItem
                              onClick={() => setCancelModal(true)}
                              icon={faTrashAlt}
                              text={'Cancel Order'}
                            />
                          )}
                          <DropdownItem
                            onClick={() =>
                              statusId === OrderStatus.Paused
                                ? usePut(`/api/order/${parseInt(order)}/mark_order_paused/`, {}, csrf)
                                : setPauseModal(true)
                            }
                            icon={faPause}
                            text={statusId === OrderStatus.Paused ? 'Unpause Order' : 'Pause Order'}
                          />
                        </PermissionGate>
                        <DropdownItem
                          onClick={() => setHotModal(true)}
                          icon={faFire}
                          hover="#ff5400"
                          text={'Make it Hot'}
                        />
                        <PermissionGate segment="outbound" minLevel="admin">
                          {statusId == OrderStatus.Shipped &&
                            (isWillCall(transportMode) || isNonParcelMode(transportMode)) && (
                              <DropdownItem
                                onClick={() => usePut(`/api/order/${parseInt(order)}/mark_order_packed/`, {}, csrf)}
                                icon={faBox}
                                text={'Revert to Packed'}
                              />
                            )}
                          <DropdownItem
                            onClick={() => usePut(`/api/order/${parseInt(order)}/mark_order_shipped/`, {}, csrf)}
                            icon={faShippingFast}
                            hover="#20b2aa"
                            text={'Mark as Shipped'}
                          />
                        </PermissionGate>
                      </Dropdown>
                    ) : null}
                  </OrderOption>
                ) : null}
                {loaded && !isWarehouse && statusId <= OrderStatus.Packed ? (
                  <OrderOption
                    onClick={() => setStatusDropdown(!statusDropdown)}
                    icon={faHeartRate}
                    title={'Change Status'}
                    arrow={true}
                    node={node}
                  >
                    {statusDropdown ? (
                      <Dropdown>
                        <PermissionGate segment="outbound" minLevel="admin">
                          {canCancel && (
                            <DropdownItem
                              onClick={() => setCancelModal(true)}
                              icon={faTrashAlt}
                              text={'Cancel Order'}
                            />
                          )}
                        </PermissionGate>
                        <DropdownItem
                          onClick={() => setHotModal(true)}
                          icon={faFire}
                          hover="#ff5400"
                          text={'Make it Hot'}
                        />
                        <PermissionGate segment="outbound" minLevel="admin">
                          <DropdownItem
                            onClick={() => usePut(`/api/order/${parseInt(order)}/mark_order_paused/`, {}, csrf)}
                            icon={faPause}
                            text={statusId === OrderStatus.Paused ? 'Unpause Order' : 'Pause Order'}
                          />
                        </PermissionGate>
                      </Dropdown>
                    ) : null}
                  </OrderOption>
                ) : null}
                {(statusId < OrderStatus.Processing || isWarehouse) && !isNonParcel(transportMode) ? (
                  <GlobalStyles.Tooltip data-title="Add additional flags for order such as Saturday Delivery">
                    <OrderOption
                      onClick={() => setShippingOptionsModal(true)}
                      icon={faFileSignature}
                      title={'Rate Options'}
                    />
                  </GlobalStyles.Tooltip>
                ) : null}
                {loaded && !resp.hasReturn && statusId >= OrderStatus.Shipped ? (
                  <GlobalStyles.Tooltip data-title="Creates return ASN">
                    <OrderOption onClick={() => setReturnModal(true)} icon={faExchange} title={'Return'} />
                  </GlobalStyles.Tooltip>
                ) : null}
                {loaded && statusId === OrderStatus.RateLimitExceeded ? (
                  <OrderOption
                    onClick={() => setOverrideShippingRateModal(true)}
                    icon={faClipboardCheck}
                    title={'Override Shipping Limit'}
                  />
                ) : null}
                {loaded && resp.integration === 'edi' && (
                  <>
                    <OrderOption
                      onClick={() => setEdiInboundModal(true)}
                      icon={faInboxIn}
                      title={'940 Warehouse Shipping Order'}
                    />
                    {statusId >= OrderStatus.Packed && (
                      <>
                        <OrderOption
                          onClick={() => setEdiOutboundModal(true)}
                          icon={faInboxOut}
                          title={'945 Warehouse Shipping Advice'}
                        />
                        <GlobalStyles.Tooltip data-title="Edit and Print SSCC Barcodes for EDI Orders">
                          <OrderOption
                            onClick={() => setBarcodeModal(true)}
                            icon={faBarcodeRead}
                            title={'SSCC Barcodes'}
                          />
                        </GlobalStyles.Tooltip>
                        <SSCCBarcodeModal open={barcodeModal} setOpen={setBarcodeModal} />
                      </>
                    )}
                  </>
                )}
                <OrderOption
                  onClick={() => setAdvancedDropdown(!advancedDropdown)}
                  icon={faTools}
                  title={'Advanced'}
                  arrow={true}
                  node={advancedNode}
                >
                  {advancedDropdown ? (
                    <Dropdown>
                      {loaded && isWarehouse && ['shopify'].includes(resp.integration) ? (
                        <GlobalStyles.Tooltip data-title="Displays Shopify integration details">
                          <DropdownItem
                            onClick={() => setIntegrationModal(true)}
                            icon={faPlug}
                            text={'Integration Details'}
                          />
                        </GlobalStyles.Tooltip>
                      ) : null}
                      {loaded && !resp.notes && !resp.batch ? (
                        <GlobalStyles.Tooltip data-title="Manually add notes to Order History">
                          <DropdownItem
                            onClick={() => setNotesModal(true)}
                            icon={faEnvelopeOpenText}
                            text={'Add Order Note'}
                          />
                        </GlobalStyles.Tooltip>
                      ) : null}
                      {loaded && resp.alternateWarehouses && resp.alternateWarehouses.length ? (
                        <>
                          <GlobalStyles.Tooltip data-title="Select warehouse for transfer ASN">
                            <DropdownItem
                              onClick={() => setAsnModal(true)}
                              icon={faRampLoading}
                              text={'Create Transfer ASN'}
                            />
                          </GlobalStyles.Tooltip>
                          {statusId < OrderStatus.Processing && (
                            <GlobalStyles.Tooltip data-title="If product is in multiples warehouses, will select best option to deliver to customer">
                              <DropdownItem
                                onClick={() => usePut(`/api/order/${order}/choose_warehouse/`, {}, csrf)}
                                icon={faRoute}
                                text={'Choose Best Warehouse'}
                              />
                            </GlobalStyles.Tooltip>
                          )}
                        </>
                      ) : null}
                      <PermissionGate segment="outbound" minLevel="admin">
                        <GlobalStyles.Tooltip data-title="Creates an identical order with new order ID">
                          <DropdownItem
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to duplicate this order?')) {
                                const resp = await usePut(`/api/order/${order}/duplicate/`, {}, csrf, false)
                                if (resp && resp.success) {
                                  navigate(`/order/${resp.duplicate_order_id}/`)
                                }
                              }
                            }}
                            icon={faClone}
                            text={'Duplicate Order'}
                          />
                        </GlobalStyles.Tooltip>
                      </PermissionGate>
                      {/* B2B settings moved into Shipment Details modal */}
                      {loaded && fulfillmentGroup && (
                        <GlobalStyles.Tooltip data-title="View fulfillment policy history and audit data as JSON">
                          <DropdownItem onClick={handleOpenFulfillmentAudit} icon={faCode} text={'Fulfillment Audit'} />
                        </GlobalStyles.Tooltip>
                      )}
                      {showPolicySelector && (
                        <GlobalStyles.Tooltip data-title="Change the fulfillment policy assigned to this order">
                          <DropdownItem
                            onClick={() => setPolicyModal(true)}
                            icon={faExchange}
                            text={'Change Fulfillment Policy'}
                          />
                        </GlobalStyles.Tooltip>
                      )}
                    </Dropdown>
                  ) : null}
                </OrderOption>
                {statusId >= OrderStatus.Picked && (
                  <OrderOption onClick={() => setLotNumbersModal(true)} icon={faInventory} title={'Lot Numbers'} />
                )}
              </ul>
              {!loaded ? <CardLoading text={placeholder} error={error} /> : null}
            </OrderOptions>
          </StickyElement>
        </Col>
        <Col lg={9} xl={7}>
          <Row>
            <Col xl={12}>
              <Row style={{ marginBottom: '20px' }}>
                <Col xl={6}>
                  <OrderInfo>
                    <OrderDetailCard {...{ order, statusId, refetchKey }} />
                  </OrderInfo>
                </Col>
                <Col xl={6}>
                  <OrderInfo>
                    <AddressCard
                      {...{ setAddressModal }}
                      address={loaded ? resp.address : {}}
                      canEditAddress={loaded ? resp.canEditAddress : false}
                      loading={!loaded}
                      error={error}
                      placeholder={placeholder}
                    />
                  </OrderInfo>
                </Col>
              </Row>
            </Col>
            {fulfillmentGroup?.orders && fulfillmentGroup.orders.length > 1 ? (
              <Col xl={12}>
                <SplitOrdersCard
                  group={fulfillmentGroup}
                  currentOrderId={order}
                  onUpdateFulfillmentItems={handleFulfillmentItemsUpdate}
                />
              </Col>
            ) : null}
            <Col xl={12}>
              {loaded && resp.notes ? (
                <BackgroundLayer>
                  <OrderNotesModal notes={resp.notes} hideModal={resp.batch} />
                </BackgroundLayer>
              ) : null}
              <BackgroundLayer>
                <TableList id={order} />
              </BackgroundLayer>
              {loaded && resp?.handling_units ? (
                <BackgroundLayer>
                  <HandlingUnitList />
                </BackgroundLayer>
              ) : null}
              <BackgroundLayer>
                <Shipping transportMode={transportMode} refetchKey={refetchKey} />
              </BackgroundLayer>
            </Col>
            <Col xl={12}>
              <Row style={{ marginBottom: '20px' }}>
                <Col xl={6}>
                  <Billing />
                </Col>
                <Col xl={6}>
                  <Slips />
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col xl={3}>
          <StickyElement>
            <NotificationList id={order} type="order" />
          </StickyElement>
        </Col>
      </Row>
      <CancelOrderModal {...{ order, cancelModal, setCancelModal, isWarehouse, statusId }} />
      <HotOrderModal {...{ order, hotModal, setHotModal }} />
      <EditProjectNotes notes="" open={notesModal} setOpen={setNotesModal} />
      <PauseOrderModal {...{ order, pauseModal, setPauseModal }} batched={loaded ? resp.batch : null} />
      <OverrideShippingRateModal {...{ order, rateAmount, overrideShippingRateModal, setOverrideShippingRateModal }} />
      <LotNumbersModal {...{ order, lotNumbersModal, setLotNumbersModal }} />
      <OrderStatusModal
        {...{ order, manualStatusModal, setManualStatusModal }}
        statusList={loaded ? resp.statusName : []}
      />
      <AddressModal
        {...{ addressModal, setAddressModal }}
        addressURL={orderPageUrl}
        address={loaded ? resp.address : {}}
      />
      <ShippingOptionsModal
        {...{ order, shippingOptionsModal, setShippingOptionsModal }}
        origShippingOptions={loaded ? resp.shippingOptions : null}
      />
      <CreateASNModal
        {...{ order, asnModal, setAsnModal }}
        alternateWarehouses={loaded ? resp.alternateWarehouses : null}
      />

      <IntegrationModal open={integrationModal} setOpen={setIntegrationModal} />
      {loaded && (
        <ShipmentManagementModal
          open={shipmentModal}
          setOpen={setShipmentModal}
          shipment={shipment}
          handlingUnitDetails={handlingUnitDetails || []}
          serviceLevels={serviceLevels || []}
          orderId={order}
          onUpdate={handleShipmentUpdate}
          b2b={b2b}
          shipBefore={shipBefore}
          shipAfter={shipAfter}
          statusId={statusId}
          isWarehouse={isWarehouse}
        />
      )}
      {loaded && resp.integration === 'edi' && (
        <>
          <WarehouseShippingOrderModal open={ediInboundModal} setOpen={setEdiInboundModal} />
          <WarehouseShippingAdviceModal open={ediOutboundModal} setOpen={setEdiOutboundModal} />
        </>
      )}
      {loaded && statusId < OrderStatus.Processing ? (
        <UploadProductModal {...{ order }} open={uploadProductModal} setOpen={setUploadProductModal} />
      ) : null}
      {loaded && statusId < OrderStatus.Shipped ? (
        <UploadLabelModal {...{ order }} open={uploadLabelModal} setOpen={setUploadLabelModal} />
      ) : null}
      {loaded && statusId < OrderStatus.Shipped ? (
        <UploadDocumentModal {...{ order }} open={uploadDocumentModal} setOpen={setUploadDocumentModal} />
      ) : null}
      <GenericModal
        heading="Fulfillment Audit"
        show={fulfillmentAuditModal}
        onHide={() => setFulfillmentAuditModal(false)}
        size="lg"
      >
        {fulfillmentAuditLoading ? <p style={{ margin: 0 }}>Loading audit details...</p> : null}
        {fulfillmentAuditError ? (
          <p style={{ color: '#f64e60', margin: '0 0 0.75rem' }} role="alert">
            {fulfillmentAuditError}
          </p>
        ) : null}
        {!fulfillmentAuditLoading && !fulfillmentAuditError && fulfillmentAuditPayload ? (
          <pre
            style={{
              background: '#f7f8ff',
              border: '1px solid #e4e6ef',
              borderRadius: '8px',
              padding: '1rem',
              maxHeight: '60vh',
              overflow: 'auto',
              margin: 0,
              fontSize: '0.875rem',
            }}
          >
            {JSON.stringify(fulfillmentAuditPayload, null, 2)}
          </pre>
        ) : null}
        {!fulfillmentAuditLoading && !fulfillmentAuditError && !fulfillmentAuditPayload ? (
          <p style={{ margin: 0 }}>No audit details available.</p>
        ) : null}
      </GenericModal>
      {showPolicySelector && (
        <GenericModal
          heading={'Change Fulfillment Policy'}
          show={policyModal}
          onHide={() => setPolicyModal(false)}
          buttons={
            <GlobalStyles.Button
              style={{ minWidth: 125 }}
              disabled={policyLoading}
              onClick={async () => {
                if (selectedPolicyId) {
                  await handlePolicyChange(selectedPolicyId)
                  setPolicyModal(false)
                }
              }}
            >
              Save
            </GlobalStyles.Button>
          }
        >
          <div style={{ width: '100%', padding: '2em 2em 3em' }}>
            <label htmlFor="fulfillment-policy-select" style={{ display: 'block', marginBottom: '0.5em' }}>
              Select the fulfillment policy to assign to this order:
            </label>
            <select
              id="fulfillment-policy-select"
              style={{ width: '100%', padding: '6px 12px', borderRadius: '4px', border: '1px solid #ced4da' }}
              value={selectedPolicyId ?? ''}
              disabled={policyLoading}
              onChange={(e) => setSelectedPolicyId(Number(e.target.value))}
            >
              <option value="" disabled>
                Select a policy...
              </option>
              {availablePolicies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </GenericModal>
      )}
      {loaded && statusId >= OrderStatus.Shipped ? <ReturnModal {...{ returnModal, setReturnModal, order }} /> : null}
    </>
  )
}
